import React from 'react';
import { AdvisorAnswer, CategoryBudget } from '../services/api';
import { formatBRL } from '../utils/format';
import { textoDoFinn } from '../utils/textoDoFinn';
import './AdvisorChat.css';

/**
 * A renderização de UMA resposta do campo de análise.
 *
 * Separada do chat de propósito: o chat cuida da conversa (lista, composição,
 * minimizar), isto cuida de mostrar um resultado. São dois trabalhos, e
 * misturá-los deixaria a lógica de mensagem embaralhada com a de formatação.
 *
 * A garantia que vale para tudo aqui: **nenhum número passou por um modelo de
 * linguagem**. O `/ask` classifica a pergunta e delega para `safeToSpend`,
 * `categoryBudget`, `applyQuery`, `buildExplanation` e `calculateProjections` —
 * os mesmos módulos que desenham as outras telas.
 */

const fmt = formatBRL;

/** '2026-08' -> 'Agosto 2026'. O resto do app fala assim; a chave crua não. */
const mesLongo = (mk?: string | null): string => {
  if (!mk) return '';
  const [y, m] = mk.split('-').map(Number);
  const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return nomes[m - 1] ? `${nomes[m - 1]} ${y}` : mk;
};


/** A compra cria o caixa negativo, ou traz para antes o que já vinha. */
const compraPioraOCaixa = (a: AdvisorAnswer): boolean =>
  !!a.cashGoesNegativeAt && (!a.wasNegativeBefore || a.cashGoesNegativeAt < a.wasNegativeBefore);

const LinhaBudget: React.FC<{ b: CategoryBudget }> = ({ b }) => (
  <>
    <div className="aa-linha">
      <span>Você gastou</span>
      <strong>{fmt(b.spent)}</strong>
    </div>
    {b.limit !== null ? (
      <div className="aa-linha">
        <span>Do seu teto de {fmt(b.limit)}</span>
        <strong className={b.remaining === 0 ? 'negativo' : undefined}>
          {b.remaining === 0 ? 'nada restante' : `${fmt(b.remaining!)} restantes`}
        </strong>
      </div>
    ) : b.typical !== null ? (
      <div className="aa-linha">
        <span>
          Você costuma gastar
          <em className="aa-nota"> mediana de {b.monthsUsed} {b.monthsUsed === 1 ? 'mês' : 'meses'} com lançamento</em>
        </span>
        <strong>{fmt(b.typical)}/mês</strong>
      </div>
    ) : (
      <div className="aa-linha"><span>Sem histórico nessa categoria para comparar</span><strong /></div>
    )}
  </>
);

const Resposta: React.FC<{ a: AdvisorAnswer }> = ({ a }) => (
  <div className="aa-card">
    <div className="aa-card-head">
      <h4>
        {a.kind === 'safe-to-spend' ? `Quanto você pode gastar${a.futuro && a.month ? ` · ${mesLongo(a.month)}` : ''}`
          : a.kind === 'category-budget' || a.kind === 'query'
            ? `${a.budget?.categoryName ?? a.categoryName ?? 'Gastos'}${a.month ? ` · ${mesLongo(a.month)}` : ''}`
          : a.kind === 'simulate' ? `${a.item ?? 'Compra'} · ${fmt(a.value ?? 0)}${a.recurring ? ' por mês' : ''}`
          : a.kind === 'explain' ? (a.explanation?.title ?? 'Como é calculado')
          : a.kind === 'conversa' ? 'Sobre as suas finanças'
          : 'Falta um dado'}
      </h4>
    </div>

    {/*
      A conversa é texto, e é o único tipo que não vira número grande.
      Os números DENTRO dela foram calculados e substituídos no servidor — o
      modelo escreveu marcadores, nunca valores, e prosa com número que não
      veio de marcador é recusada inteira antes de chegar aqui.
    */}
    {a.kind === 'conversa' && a.texto && (
      <div className="aa-conversa">{textoDoFinn(a.texto)}</div>
    )}

    {a.kind === 'incomplete' && (
      <div className="aa-vazio">
        {a.missing.includes('value') && 'Me diga quanto custa e eu simulo o impacto.'}
        {a.missing.includes('categoryId') && 'Não achei essa categoria no seu cadastro.'}
      </div>
    )}

    {a.kind === 'safe-to-spend' && a.safeToSpend && (
      <>
        <div className="aa-linha aa-linha-destaque">
          <span>Disponível</span>
          <strong>{fmt(a.safeToSpend.available)}</strong>
        </div>
        <div className="aa-conta">
          <div><span>Recebimentos do mês</span><span>{fmt(a.safeToSpend.income)}</span></div>
          <div><span>− Contas fixas e parcelas</span><span>{fmt(a.safeToSpend.committed)}</span></div>
          <div><span>{a.futuro ? '− Avulsas já lançadas' : '− Já gasto'}</span><span>{fmt(a.safeToSpend.alreadySpent)}</span></div>
        </div>
        {/* O caixa ao lado do fluxo do mês (13/10): mês negativo com dinheiro
            na conta não é "sem dinheiro". */}
        {typeof a.emCaixa === 'number' && (
          <div className="aa-linha">
            <span>{a.futuro && a.month ? `Em caixa ao chegar a ${mesLongo(a.month).split(' ')[0].toLowerCase()} (projeção)` : 'Em caixa hoje'}</span>
            <strong>{fmt(a.emCaixa)}</strong>
          </div>
        )}
        {a.notaDoCaixa && <div className="aa-nota-bloco">{a.notaDoCaixa}</div>}
        {a.safeToSpend.paceRemaining !== null && a.safeToSpend.paceRemaining > 0 && (
          <div className="aa-nota-bloco">
            No seu ritmo normal, {fmt(a.safeToSpend.paceRemaining)} disso já têm destino.
          </div>
        )}
      </>
    )}

    {a.kind === 'category-budget' && a.budget && <LinhaBudget b={a.budget} />}

    {/* D5: quando a pergunta cita categoria e período, as duas leituras vêm
        juntas — o que saiu e o que ainda cabe. Assim a leitura errada do
        modelo deixa de ter consequência. */}
    {a.kind === 'query' && (
      <>
        {/* Com as duas leituras (D5), a linha do orçamento já mostra o total.
            Repetir o mesmo número duas vezes faz o leitor procurar a diferença
            que não existe. */}
        {a.budget ? (
          <>
            <div className="aa-linha">
              <span>{a.count} {a.count === 1 ? 'lançamento' : 'lançamentos'}</span>
              <strong />
            </div>
            <LinhaBudget b={a.budget} />
          </>
        ) : (
          <div className="aa-linha">
            <span>{a.count} {a.count === 1 ? 'lançamento' : 'lançamentos'}</span>
            <strong>{fmt(a.total ?? 0)}</strong>
          </div>
        )}
      </>
    )}

    {a.kind === 'simulate' && a.delta && (
      <>
        {/* A resposta ao "posso?", primeiro: no mês, ou no caixa (13/10). */}
        {a.cabe && (
          <div className={`aa-nota-bloco${a.cabe.noMes || a.cabe.noCaixa ? '' : ' alerta'}`}>{a.cabe.frase}</div>
        )}
        <div className="aa-linha aa-linha-destaque">
          <span>Hoje você pode gastar</span>
          <strong>{fmt(a.before?.available ?? 0)}</strong>
        </div>
        <div className="aa-conta">
          {a.delta.slice(0, 4).map(d => (
            <div key={d.month}>
              <span>{d.monthLabel}</span>
              <span className={d.cumulativeCashBalance < 0 ? 'negativo' : undefined}>
                {fmt(d.cumulativeCashBalance)} <em>({fmt(d.difference)})</em>
              </span>
            </div>
          ))}
        </div>
        {/* Antecipar o vermelho (28/10) é culpa da compra, como criá-lo: com
            janeiro já negativo, a compra que leva o caixa a dezembro negativo
            mudava a resposta e o card dizia "com ou sem esta compra". */}
        <div className={`aa-nota-bloco${compraPioraOCaixa(a) ? ' alerta' : ''}`}>
          {a.cashGoesNegativeAt && !a.wasNegativeBefore
            ? `Esta compra faz seu caixa ficar negativo em ${mesLongo(a.cashGoesNegativeAt)}.`
            : compraPioraOCaixa(a)
              ? `Esta compra antecipa o caixa negativo para ${mesLongo(a.cashGoesNegativeAt)}. Sem ela, ficaria negativo só em ${mesLongo(a.wasNegativeBefore)}.`
              : a.wasNegativeBefore
                ? `Seu caixa já ficava negativo em ${mesLongo(a.wasNegativeBefore)}, com ou sem esta compra.`
                : 'Seu caixa não fica negativo no período projetado.'}
        </div>
        <div className="aa-nota-bloco">Nada foi gravado. Isto é só uma simulação.</div>
      </>
    )}

    {a.kind === 'explain' && a.explanation && (
      <div className="aa-explicacao">
        {a.explanation.note.split('\n\n').map((p, i) => <p key={i}>{p.replace(/\*\*/g, '')}</p>)}
      </div>
    )}

    {a.unresolved && (
      <div className="aa-aviso">Não encontrei “{a.unresolved}” no seu cadastro.</div>
    )}
  </div>
);

export { Resposta as default, mesLongo, fmt };
